// Conflict-aware list writes (Walter 2026-05-16, "B mechanism").
//
// On every prose write (content/title), we run a Firestore transaction:
//   1. Read the current cloud doc inside the tx.
//   2. If `cloud.updatedAt > baselineUpdatedAtMs` (the last server-confirmed
//      timestamp we saw for this list), the doc was modified by another
//      device while we were composing our write. That's a conflict.
//   3. On conflict, write the about-to-be-overwritten content+title into
//      lists/{id}/history/{autoId} BEFORE applying our patch. The user's
//      previous version becomes recoverable from the side-by-side
//      HistoryPanel; nothing ever silently vanishes.
//   4. Apply our patch.
//
// The list doc also gets `historyCount` incremented so the UI can show
// a "previous versions" icon without subscribing to the subcollection
// on every list. We only pay the storage/write cost when there IS a
// conflict — non-conflicting writes are still single-doc setDocs.
//
// What this DOES NOT do:
//   - Auto-merge content. Last-writer-wins still applies to the live doc;
//     this just makes the loser recoverable.
//   - Block writes. There's no "are you sure" UI on conflict — we just
//     snapshot and warn via toast.
//   - Handle non-prose conflicts. Position/state/z still write straight
//     through (no semantic merge possible, no data loss to recover from).
//
// The offline-queue replay path (lib/conflictResolver.js) still uses its
// own prepend-marker auto-merge — that's a different surface, dealing
// with edits the user made offline against a known-stale baseline. This
// helper is for the ONLINE-but-racy case where the lock didn't catch a
// concurrent editor.

import {
  doc, collection, runTransaction, serverTimestamp, increment,
  query, orderBy, limit, getDocs, setDoc, deleteDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import { getClientId } from './clientId';
import { mergeThreeWay } from './threeWayMerge';

const PROSE_FIELDS = ['content', 'title'];

// Walter 2026-06-16: history retention. Originally "keep newest 30 by
// count". Walter 2026-07-19 (sync-hardening round): TIME-based policy —
// keep everything younger than 30 DAYS, but always keep the newest 5
// versions even when older, with a hard safety cap of 100 so a runaway
// conflict loop can't grow a list's history unbounded within the window.
// Still a natural premium lever later (deeper window on paid tiers).
export const HISTORY_RETENTION_DAYS = 30;
export const HISTORY_RETENTION_MIN_KEEP = 5;
export const HISTORY_RETENTION_HARD_CAP = 300;

/**
 * How often a list takes an automatic snapshot while it's being edited.
 *
 * Walter 2026-09-02, after losing most of a paragraph and finding the nearest
 * recoverable version hours old: *"the history is too coarse. Shouldn't it
 * save a lot more snapshots?"* He's right. This was 24h, which meant the
 * safety net below a conflict or a >50% deletion was a full day wide — a
 * quietly vanished paragraph fell straight through it.
 *
 * 10 minutes of ACTIVE EDITING (the gate also requires the content to be
 * changing and non-empty), so an untouched list still costs nothing. An hour
 * of writing adds ~6 history docs; a heavy day across five lists is a few
 * dozen small writes — negligible against Firestore's per-write price, and
 * cheap next to losing a paragraph.
 */
export const AUTO_SNAPSHOT_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Beyond 48h, thin automatic snapshots to one per day. The dense recent
 * history is what recovers today's accident; a week later, one point per day
 * is all anyone actually uses, and keeping 6/hour forever would blow the cap
 * and push out the older days that give history its reach.
 */
export const HISTORY_DENSE_WINDOW_MS = 48 * 60 * 60 * 1000;

function patchTouchesProse(patch) {
  if (!patch) return false;
  return PROSE_FIELDS.some(k => k in patch);
}

/**
 * pruneHistoryToCap — apply the retention policy to a list's history,
 * newest-first (ordered by replacedAt desc):
 *   - the newest HISTORY_RETENTION_MIN_KEEP (5) are ALWAYS kept, any age;
 *   - beyond those, entries younger than HISTORY_RETENTION_DAYS (30) are
 *     kept up to HISTORY_RETENTION_HARD_CAP (100) total;
 *   - everything else is deleted.
 * Best-effort + fire-and-forget: called after a history entry is added;
 * failures are swallowed (a prune miss just leaves a few extra entries,
 * never blocks a save). Every snapshot we write stamps replacedAt; any
 * legacy doc missing the field is skipped by the orderBy (harmless).
 */
export async function pruneHistoryToCap({ uid, boardId, listId }) {
  if (!uid || !boardId || !listId) return;
  try {
    const histColRef = collection(
      db, 'users', uid, 'boards', boardId, 'lists', listId, 'history',
    );
    const snap = await getDocs(query(histColRef, orderBy('replacedAt', 'desc')));
    if (snap.size <= HISTORY_RETENTION_MIN_KEEP) return;
    const now = Date.now();
    const cutoffMs = now - HISTORY_RETENTION_DAYS * 24 * 60 * 60 * 1000;
    const denseCutoffMs = now - HISTORY_DENSE_WINDOW_MS;
    // Walter 2026-09-02: snapshots are now every 10 minutes of editing, so
    // "keep the newest 100" would be a few hours of one busy list and would
    // evict every older day. Tiered instead: keep everything recent, then one
    // AUTOMATIC snapshot per day. Anything the user or a real event produced
    // (conflict, divergence, restore, resolution, shrink-guard) is never
    // thinned — those are the ones someone deliberately needs.
    const keptDays = new Set();
    const surplus = snap.docs.filter((d, idx) => {
      if (idx < HISTORY_RETENTION_MIN_KEEP) return false;      // newest 5: keep, any age
      if (idx >= HISTORY_RETENTION_HARD_CAP) return true;      // beyond hard cap: delete
      const data = d.data();
      const ms = data.replacedAt?.toMillis?.() ?? null;
      if (ms != null && ms < cutoffMs) return true;            // older than 30d: delete
      const isAuto = (data.reason || 'daily') === 'daily';
      if (!isAuto) return false;                               // event snapshots: always keep
      if (ms == null || ms >= denseCutoffMs) return false;     // last 48h: keep them all
      const day = new Date(ms).toISOString().slice(0, 10);     // older: one per day
      if (keptDays.has(day)) return true;
      keptDays.add(day);
      return false;
    });
    if (surplus.length === 0) return;
    await Promise.all(surplus.map(d => deleteDoc(d.ref)));
  } catch (e) {
    // Non-fatal — retention is a housekeeping nicety, not correctness.
    console.warn('[pruneHistoryToCap] failed (non-fatal)', e);
  }
}

// Walter 2026-06-15 — content-shrink guard (the "events that should
// trigger a snapshot" safety net). The silent-loss class Walter hit was
// a stale-buffer / dormant-device write that overwrote a full note with
// much less content WITHOUT tripping the timestamp conflict check (the
// baseline had advanced on wake, or the write looked self-originated).
//
// So in addition to the conflict path, we snapshot purely on the SHAPE
// of the write: if an incoming content write drops a substantial amount
// of existing text — more than half of it AND at least ~60 chars — we
// archive the about-to-be-lost version to history before overwriting.
// This is content-based, not timestamp-based, so it catches the silent
// overwrite that the conflict detector misses. It also (intentionally)
// makes a deliberate "clear the whole list" recoverable from the
// previous-versions panel. Normal incremental edits never come close to
// the threshold, so the cost is paid only when a lot of text vanishes.
//
// Walter 2026-06-15: lowered 200→40. Original floor was too high — a
// quick test "paragraph" deleted whole didn't trip it, so Walter saw no
// history. 40 chars is "about a sentence"; deleting a paragraph or most
// of a list now snapshots, while trimming one short line stays silent.
// The ratio guard (<=half) keeps partial edits that keep most of the
// text from firing, so this stays a "lost most of it" signal.
const SHRINK_GUARD_MIN_PREV = 40; // ignore tiny lists — little to lose
const SHRINK_GUARD_MIN_DROP = 40; // must drop at least this many chars
const SHRINK_GUARD_MAX_RATIO = 0.5; // result must be <= half the original

function isBigContentShrink(prevContent, nextContent) {
  const prevLen = (prevContent ?? '').length;
  const nextLen = (nextContent ?? '').length;
  if (prevLen < SHRINK_GUARD_MIN_PREV) return false;
  const dropped = prevLen - nextLen;
  if (dropped < SHRINK_GUARD_MIN_DROP) return false;
  return nextLen <= prevLen * SHRINK_GUARD_MAX_RATIO;
}

/**
 * writeListWithConflictDetection
 *
 * Drop-in replacement for setDoc on the list doc when the caller has a
 * baseline updatedAt to compare against. If the patch doesn't touch
 * prose, we skip the conflict check entirely (last-write-wins on non-
 * prose is safe).
 *
 * Walter 2026-06-15 — CONTENT-LEVEL detection. The original check compared
 * timestamps (`cur.updatedAt > baselineUpdatedAtMs`). That has a fatal gap:
 * the local timestamp baseline (lastServerUpdatedAt in useData) advances on
 * EVERY incoming server snapshot, even while the editor is actively held on
 * the user's in-progress buffer and has NOT adopted the incoming content.
 * So a dormant/second device could overwrite a fresh edit while the
 * timestamps looked equal — silent loss, no conflict flag (exactly what
 * lost Walter's notes). The robust signal is a 3-way content compare:
 *
 *   base   = the cloud content the editor's buffer actually descends from
 *            (captured editor-side at adopt/save time — NOT the timestamp
 *            baseline, which advances independently). Passed as baselineContent.
 *   theirs = cur.content (what the cloud has right now, read in the tx)
 *   mine   = patch.content (what we're about to write)
 *
 * If theirs !== base, the cloud diverged from what we started editing → real
 * conflict regardless of timestamps. We snapshot theirs to history, then
 * write mine (last-writer-wins on the live doc, loser recoverable). When
 * baselineContent is provided it SUPERSEDES the timestamp check — which also
 * removes the old false-positive class (timestamp moved but content didn't).
 * Callers that don't pass it fall back to the timestamp check, unchanged.
 *
 * @param {Object} args
 * @param {string} args.uid             owner uid (the boards subtree's owner)
 * @param {string} args.boardId
 * @param {string} args.listId
 * @param {Object} args.patch
 * @param {number|null} args.baselineUpdatedAtMs  last server-confirmed updatedAt for this list, in ms
 * @param {string|undefined} args.baselineContent the cloud content this edit descends from (3-way base)
 * @param {string} args.actorUid        who is performing the write (for audit)
 *
 * @returns {Promise<{
 *   hadConflict: boolean,
 *   historyId: string|null,
 *   prevContent: string,
 *   prevTitle: string,
 *   prevUpdatedAtMs: number|null,
 * }>}
 */
export async function writeListWithConflictDetection({
  uid, boardId, listId, patch, baselineUpdatedAtMs, baselineContent, actorUid,
}) {
  const empty = {
    hadConflict: false,
    historyId: null,
    prevContent: '',
    prevTitle: '',
    prevUpdatedAtMs: null,
  };
  if (!uid || !boardId || !listId) return empty;

  const ref = doc(db, 'users', uid, 'boards', boardId, 'lists', listId);
  const histColRef = collection(
    db, 'users', uid, 'boards', boardId, 'lists', listId, 'history',
  );
  const newHistRef = doc(histColRef);
  const clientId = getClientId();
  const touchesProse = patchTouchesProse(patch);

  const result = await runTransaction(db, async (t) => {
    const snap = await t.get(ref);
    const cur = snap.exists() ? snap.data() : {};
    const curUpdatedAtMs = cur.updatedAt?.toMillis?.() ?? null;

    // Walter 2026-05-18: defensive — if the patch's prose fields are
    // IDENTICAL to what the cloud already has, this is a no-op write
    // (e.g. a stale-baseline migration like the legacy glyph-strip
    // that the new content didn't actually need). Don't flag a
    // conflict for a no-op, and skip the write entirely.
    if (touchesProse) {
      const patchContentEq = !('content' in patch) || (patch.content ?? '') === (cur.content ?? '');
      const patchTitleEq = !('title' in patch) || (patch.title ?? '') === (cur.title ?? '');
      if (patchContentEq && patchTitleEq) {
        // No-op for prose. If there are non-prose fields in the
        // patch, still write those.
        const nonProse = Object.fromEntries(
          Object.entries(patch).filter(([k]) => !PROSE_FIELDS.includes(k)),
        );
        if (Object.keys(nonProse).length > 0) {
          t.set(ref, {
            ...nonProse,
            updatedAt: serverTimestamp(),
            lastWriteByClientId: clientId,
          }, { merge: true });
        }
        return empty;
      }
    }

    // Walter 2026-05-19: skip the conflict flag if the most recent
    // write to the cloud came from THIS device. Otherwise we
    // self-conflict whenever the listener echo of our own prior
    // write races our next write — Walter caught this immediately
    // after resolving a real conflict, then typing on the same
    // device: the resolve-clear write bumped updatedAt, the listener
    // hadn't fully refreshed baseline yet, and the next typing write
    // flagged a phantom conflict. The clientId check makes
    // "another device wrote" the actual signal, not "the cloud
    // moved forward."
    const lastWriter = cur.lastWriteByClientId || null;
    const isSelfRecentWrite = lastWriter && lastWriter === clientId;

    // Content-level (3-way) conflict — the primary signal when the caller
    // supplies baselineContent. theirs !== base means the cloud changed
    // out from under the buffer this edit descends from. We still require
    // we're actually changing the content (mine !== theirs) and that the
    // last cloud write wasn't our own echo (same clientId), which keeps
    // the resolve-then-keep-typing case from self-flagging.
    const touchesContent = patch && ('content' in patch);
    const hasContentBaseline = touchesContent && typeof baselineContent === 'string';
    const curContent = cur.content ?? '';
    const contentConflict = (
      hasContentBaseline
      && curContent !== baselineContent
      && (patch.content ?? '') !== curContent
      && !isSelfRecentWrite
    );

    // Timestamp fallback — used only when we have NO content baseline
    // (older callers, title-only edits). Walter 2026-05-19 self-write
    // suppression retained.
    const timestampConflict = (
      touchesProse
      && baselineUpdatedAtMs != null
      && curUpdatedAtMs != null
      && curUpdatedAtMs > baselineUpdatedAtMs
      && !isSelfRecentWrite
    );

    // Prefer the content signal whenever we have it — it's both more
    // sensitive (catches the silent timestamp-equal overwrite) and more
    // specific (no false positive when the timestamp moved but content
    // didn't). Fall back to timestamp only without a content baseline.
    const hasConflict = hasContentBaseline ? contentConflict : timestampConflict;

    if (hasConflict) {
      // 3-way CLEAN auto-merge (Walter 2026-07-13). Before surfacing the
      // conflict band, try the same line-aware merge the offline-queue flush
      // uses. When the two devices' edits don't overlap (disjoint lines, or one
      // side deleted a line the other left alone), the merge is CLEAN and we
      // apply it SILENTLY — no history snapshot, no conflictPendingReview. Only
      // a genuine same-line clash falls through to the review flow below. The
      // caller's local buffer reconciles to `merged` via the snapshot listener,
      // exactly as it does for any other remote update.
      if (hasContentBaseline) {
        const { merged, clean } = mergeThreeWay(baselineContent, patch.content ?? '', curContent);
        if (clean) {
          t.set(ref, {
            ...patch,
            content: merged,
            updatedAt: serverTimestamp(),
            lastWriteByClientId: clientId,
          }, { merge: true });
          return { ...empty, autoMerged: true, mergedContent: merged };
        }
      }
      t.set(newHistRef, {
        content: cur.content ?? '',
        title: cur.title ?? '',
        prevUpdatedAt: cur.updatedAt ?? null,
        replacedAt: serverTimestamp(),
        replacedByUid: actorUid || null,
        replacedByClientId: clientId,
        // Distinguishes a real two-device collision from the shrink-guard
        // / restore / resolution snapshots in the history panel.
        reason: 'conflict',
        // Walter 2026-06-22: stash the 3-way BASE (the content both versions
        // descended from) so the conflict modal's "merge both (beta)" button
        // can run a real 3-way merge. Null when no content baseline was passed.
        baseContent: hasContentBaseline ? (baselineContent ?? '') : null,
      });
      // Merge the count + the conflictPendingReview flag into our main
      // write so we only modify the list doc once in this transaction
      // (Firestore allows only one mutation per doc per tx). The flag
      // is what drives the in-tile band + history icon — cleared
      // when the user resolves via the modal.
      t.set(
        ref,
        {
          ...patch,
          updatedAt: serverTimestamp(),
          historyCount: increment(1),
          conflictPendingReview: serverTimestamp(),
          lastWriteByClientId: clientId,
        },
        { merge: true },
      );
      return {
        hadConflict: true,
        historyId: newHistRef.id,
        prevContent: cur.content ?? '',
        prevTitle: cur.title ?? '',
        prevUpdatedAtMs: curUpdatedAtMs,
      };
    }

    // No detected conflict — but before applying, run the content-shrink
    // guard. If this write wipes out a substantial amount of existing
    // content (see isBigContentShrink), snapshot the current version so
    // the loss is recoverable even though no conflict was flagged. This
    // is the backstop for the silent stale-buffer overwrite.
    if (
      touchesProse
      && 'content' in patch
      && isBigContentShrink(cur.content, patch.content)
    ) {
      t.set(newHistRef, {
        content: cur.content ?? '',
        title: cur.title ?? '',
        prevUpdatedAt: cur.updatedAt ?? null,
        replacedAt: serverTimestamp(),
        replacedByUid: actorUid || null,
        replacedByClientId: clientId,
        // Distinguishes a shrink-guard snapshot from a real conflict in
        // the history panel — this one was a precautionary archive of a
        // large deletion, not a two-device collision.
        reason: 'shrink-guard',
      });
      t.set(ref, {
        ...patch,
        updatedAt: serverTimestamp(),
        historyCount: increment(1),
        lastWriteByClientId: clientId,
      }, { merge: true });
      return {
        hadConflict: false,
        historyId: newHistRef.id,
        prevContent: cur.content ?? '',
        prevTitle: cur.title ?? '',
        prevUpdatedAtMs: curUpdatedAtMs,
        snapshotReason: 'shrink-guard',
      };
    }

    // Walter 2026-06-16 (#4): daily rolling snapshot. Independent of
    // conflicts/large-deletions, keep a recent recoverable point: when a
    // content write lands and it's been ≥24h since this list's last daily
    // snapshot, archive the PRE-write version first. Bounded to once/24h
    // per list (gated on lastDailySnapshotAt) and only when content is
    // actually changing + non-empty, so it adds ~one history entry per
    // active day, pruned by the retention cap. Labelled "(daily backup)".
    const contentChanging = 'content' in patch && (patch.content ?? '') !== curContent;
    const lastDailyMs = cur.lastDailySnapshotAt?.toMillis?.() ?? 0;
    const wantDaily = contentChanging && curContent.length > 0
      && (Date.now() - lastDailyMs) >= AUTO_SNAPSHOT_INTERVAL_MS;
    if (wantDaily) {
      t.set(newHistRef, {
        content: cur.content ?? '',
        title: cur.title ?? '',
        prevUpdatedAt: cur.updatedAt ?? null,
        replacedAt: serverTimestamp(),
        replacedByUid: actorUid || null,
        replacedByClientId: clientId,
        reason: 'daily',
      });
      t.set(ref, {
        ...patch,
        updatedAt: serverTimestamp(),
        historyCount: increment(1),
        lastDailySnapshotAt: serverTimestamp(),
        lastWriteByClientId: clientId,
      }, { merge: true });
      return {
        hadConflict: false,
        historyId: newHistRef.id,
        prevContent: cur.content ?? '',
        prevTitle: cur.title ?? '',
        prevUpdatedAtMs: curUpdatedAtMs,
        snapshotReason: 'daily',
      };
    }

    // No conflict — normal write path. Stamp lastWriteByClientId so
    // a subsequent same-device write can be recognised as a non-
    // conflict even if the listener echo of THIS write hasn't
    // refreshed local baseline yet.
    t.set(ref, {
      ...patch,
      updatedAt: serverTimestamp(),
      lastWriteByClientId: clientId,
    }, { merge: true });
    return empty;
  });

  // If this write added a history snapshot (conflict or shrink-guard),
  // prune the list's history back to the retention cap. Fire-and-forget so
  // it never delays the save; pruneHistoryToCap swallows its own errors.
  if (result && result.historyId) {
    pruneHistoryToCap({ uid, boardId, listId });
  }
  return result;
}

/**
 * archiveDivergedLocal — the merge-on-receive WRITE-AHEAD BACKSTOP.
 * Walter 2026-08-07; plan: docs/proposals/2026-07-19-sync-hardening-merge-on-
 * receive.md.
 *
 * Called by the panels' snapshot-sync effect when an arriving server version
 * and our local buffer BOTH moved away from the shared base and the 3-way
 * merge is NOT clean (a genuine same-line clash). Before the effect drops the
 * local buffer to display the server value, that buffer is archived here so
 * nothing the user typed can vanish — this is the receive-side twin of the
 * snapshot `writeListWithConflictDetection` takes on the write side.
 *
 * Two writes, one transaction:
 *   1. a history doc holding OUR diverged content, `reason: 'divergence'`,
 *      plus the 3-way `baseContent` so the modal's "merge both" can run;
 *   2. a merge onto the list doc raising `conflictPendingReview` (the band)
 *      and bumping `historyCount` (the ↶ icon).
 *
 * NOTE — no `updatedAt` bump. The list's CONTENT is untouched here (we're
 * archiving our own copy, not writing it); bumping the doc's mtime would make
 * other devices' timestamp-fallback conflict check fire on a bookkeeping
 * write. `lastWriteByClientId` IS stamped so our own next save recognises the
 * echo as self-originated and doesn't cascade a second conflict.
 *
 * @param {Object} args
 * @param {string} args.uid          owner uid (the owner-path uid — placed
 *                                   shared lists live under the OWNER's
 *                                   subtree; the panels pass the same tuple
 *                                   the edit-lock uses)
 * @param {string} args.boardId
 * @param {string} args.listId
 * @param {string} args.content      the diverged local buffer to preserve
 * @param {string} args.baseContent  the 3-way base both versions descend from
 * @param {string} args.actorUid     who was editing (for audit)
 *
 * @returns {Promise<{ historyId: string|null }>} historyId is null when
 *   nothing was archived (missing path, or the list doc is gone) — callers
 *   MUST treat that as "not safe to drop the buffer yet".
 */
export async function archiveDivergedLocal({
  uid, boardId, listId, content, baseContent, actorUid,
}) {
  if (!uid || !boardId || !listId) return { historyId: null };
  const ref = doc(db, 'users', uid, 'boards', boardId, 'lists', listId);
  const histColRef = collection(
    db, 'users', uid, 'boards', boardId, 'lists', listId, 'history',
  );
  const newHistRef = doc(histColRef);
  const clientId = getClientId();

  const archived = await runTransaction(db, async (t) => {
    const snap = await t.get(ref);
    // List deleted underneath us — an orphaned history doc would be
    // unreachable, so archive nothing and let the caller keep the buffer.
    if (!snap.exists()) return false;
    const cur = snap.data();
    t.set(newHistRef, {
      content: content ?? '',
      title: cur.title ?? '',
      prevUpdatedAt: cur.updatedAt ?? null,
      replacedAt: serverTimestamp(),
      replacedByUid: actorUid || null,
      replacedByClientId: clientId,
      // Distinguishes "this device's unsynced version" from a write-path
      // conflict / shrink-guard / daily snapshot in the history panel.
      reason: 'divergence',
      baseContent: typeof baseContent === 'string' ? baseContent : null,
    });
    t.set(
      ref,
      {
        historyCount: increment(1),
        conflictPendingReview: serverTimestamp(),
        lastWriteByClientId: clientId,
      },
      { merge: true },
    );
    return true;
  });

  if (!archived) return { historyId: null };
  pruneHistoryToCap({ uid, boardId, listId });
  return { historyId: newHistRef.id };
}

/**
 * archivePreMerge — snapshot a list's text BEFORE a silent automatic merge.
 *
 * Walter 2026-09-03, from the content-writer audit. Both auto-merge paths —
 * the offline-queue replay in lib/conflictResolver and merge-on-receive in the
 * panels — apply a CLEAN three-way merge with no band and, until now, no
 * history entry. The comment in conflictResolver says so outright: *"the merge
 * is CLEAN and we apply it SILENTLY (no band, no history snapshot)"*. That is
 * the right behaviour — it's what lets a dormant device wake up and resolve
 * invisibly — but "clean" means the ALGORITHM found no overlapping edits, not
 * that the result is right. `threeWayMerge` reads a moved block as a delete
 * plus an insert (#168), so a both-sides reorder can misjudge; and when it
 * misjudges on a clean verdict, lines vanish leaving NOTHING behind. No band,
 * no snapshot, nothing in history to recover from. Walter lost most of a
 * paragraph that way and the nearest recoverable version was hours old.
 *
 * So: one small write before every silent merge, and the pre-merge text is
 * always recoverable. Unlike archiveDivergedLocal this does NOT set
 * conflictPendingReview — the whole point is that the merge stays invisible;
 * we're buying a safety net, not a notification.
 *
 * Cheap by construction: only fires on an actual auto-merge, which needs both
 * sides to have changed.
 */
export async function archivePreMerge({
  uid, boardId, listId, content, baseContent, actorUid, source,
}) {
  if (!uid || !boardId || !listId) return { historyId: null };
  try {
    const ref = doc(db, 'users', uid, 'boards', boardId, 'lists', listId);
    const histColRef = collection(
      db, 'users', uid, 'boards', boardId, 'lists', listId, 'history',
    );
    const newHistRef = doc(histColRef);
    const clientId = getClientId();
    const archived = await runTransaction(db, async (t) => {
      const snap = await t.get(ref);
      if (!snap.exists()) return false;
      const cur = snap.data();
      t.set(newHistRef, {
        content: typeof content === 'string' ? content : (cur.content ?? ''),
        title: cur.title ?? '',
        prevUpdatedAt: cur.updatedAt ?? null,
        replacedAt: serverTimestamp(),
        replacedByUid: actorUid || null,
        replacedByClientId: clientId,
        reason: 'pre-merge',
        baseContent: typeof baseContent === 'string' ? baseContent : null,
        mergeSource: source || null,
      });
      t.set(ref, { historyCount: increment(1) }, { merge: true });
      return true;
    });
    if (!archived) return { historyId: null };
    pruneHistoryToCap({ uid, boardId, listId });
    return { historyId: newHistRef.id };
  } catch (e) {
    // Never let the safety net block the merge it is protecting.
    console.warn('[archivePreMerge] failed (non-fatal)', e);
    return { historyId: null };
  }
}

/**
 * restoreHistoryEntry — write a stored history entry's content/title
 * back onto the live list doc, while ALSO snapshotting whatever the
 * live doc currently has into a new history entry. So restoring is
 * itself non-destructive: if the user picks the wrong version, they
 * can restore the version that just got displaced.
 *
 * @param {Object} args
 * @param {string} args.uid
 * @param {string} args.boardId
 * @param {string} args.listId
 * @param {Object} args.entry              the history entry to restore
 * @param {string} args.entry.content
 * @param {string} args.entry.title
 * @param {string} args.actorUid           who is restoring (for audit)
 *
 * @returns {Promise<{ historyId: string }>}  id of the new "pre-restore"
 *   history entry containing what the list looked like just before this
 *   restore took effect.
 */
export async function restoreHistoryEntry({
  uid, boardId, listId, entry, actorUid,
}) {
  if (!uid || !boardId || !listId || !entry) {
    return { historyId: null };
  }
  const ref = doc(db, 'users', uid, 'boards', boardId, 'lists', listId);
  const histColRef = collection(
    db, 'users', uid, 'boards', boardId, 'lists', listId, 'history',
  );
  const newHistRef = doc(histColRef);
  const clientId = getClientId();

  await runTransaction(db, async (t) => {
    const snap = await t.get(ref);
    const cur = snap.exists() ? snap.data() : {};
    t.set(newHistRef, {
      content: cur.content ?? '',
      title: cur.title ?? '',
      prevUpdatedAt: cur.updatedAt ?? null,
      replacedAt: serverTimestamp(),
      replacedByUid: actorUid || null,
      replacedByClientId: clientId,
      // Marks this snapshot as "the version we displaced during a restore"
      // so the panel can label it accordingly.
      reason: 'restore',
    });
    t.set(
      ref,
      {
        content: entry.content ?? '',
        title: entry.title ?? cur.title ?? '',
        updatedAt: serverTimestamp(),
        historyCount: increment(1),
        // Walter 2026-05-17: restore is also a resolution action.
        // Clears the band so the user doesn't have to do it twice.
        conflictPendingReview: null,
        lastWriteByClientId: clientId,
      },
      { merge: true },
    );
  });
  pruneHistoryToCap({ uid, boardId, listId });
  return { historyId: newHistRef.id };
}

/**
 * duplicateAsNewList — save a history entry's content+title as a brand-new
 * list, leaving the source list untouched. Used by HistoryPanel's
 * "keep both — save as new list" action when the user wants to preserve
 * a previous version side-by-side with the current one rather than
 * replacing.
 *
 * @param {Object} args
 * @param {string} args.uid             owner uid (same subtree the source is in)
 * @param {string} args.boardId         board id (same as source)
 * @param {Object} args.entry           history entry { content, title }
 * @param {Object} args.sourceList      live list doc (for title + position seed)
 *
 * @returns {Promise<{ id: string }>}  the new list's id
 */
export async function duplicateAsNewList({ uid, boardId, entry, sourceList }) {
  if (!uid || !boardId || !entry) return { id: null };
  const colRef = collection(db, 'users', uid, 'boards', boardId, 'lists');
  const newRef = doc(colRef);
  const baseTitle = (entry.title || sourceList?.title || 'untitled').trim();
  // Suffix "(restored)" so the duplicate is obvious in the sidebar.
  const title = baseTitle ? `${baseTitle} (restored)` : '(restored)';
  // Seed position from the source list, nudged so the duplicate doesn't
  // sit exactly on top in free mode. Grid mode rebalances on render.
  const x = (sourceList?.x ?? 80) + 40;
  const y = (sourceList?.y ?? 60) + 40;
  await setDoc(newRef, {
    title,
    content: entry.content ?? '',
    x, y,
    w: sourceList?.w ?? 280,
    h: sourceList?.h ?? 320,
    z: (sourceList?.z ?? 0) + 1,
    order: (sourceList?.order ?? 0) + 0.5,
    state: 'open',
    gridCol: sourceList?.gridCol ?? 0,
    gridRow: (sourceList?.gridRow ?? 0) + 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastWriteByClientId: getClientId(),
  });
  return { id: newRef.id };
}

/**
 * deleteListNow — hard-delete a list doc (used by the "save as new list"
 * undo toast to remove the just-created duplicate). NOT routed through
 * the trashList Cloud Function because this is an immediate undo of a
 * user-initiated create, not a regular delete; trash is overkill and
 * adds latency the undo can't afford.
 */
export async function deleteListNow({ uid, boardId, listId }) {
  if (!uid || !boardId || !listId) return;
  const ref = doc(db, 'users', uid, 'boards', boardId, 'lists', listId);
  await deleteDoc(ref);
}

/**
 * clearConflictPendingReview — clear the conflictPendingReview flag
 * on a list doc. Used by the "mark as resolved" action in
 * HistoryPanel to dismiss the band + history icon without changing
 * content. The history archive itself is unaffected.
 *
 * If `cleanContent` is provided, it's written to the doc alongside
 * (used to also strip any legacy `// CONFLICTING EDITS` marker from
 * an existing list during the "mark resolved" flow).
 */
export async function clearConflictPendingReview({ uid, boardId, listId, cleanContent, actorUid }) {
  if (!uid || !boardId || !listId) return;
  const ref = doc(db, 'users', uid, 'boards', boardId, 'lists', listId);
  const histColRef = collection(
    db, 'users', uid, 'boards', boardId, 'lists', listId, 'history',
  );
  const newHistRef = doc(histColRef);
  const clientId = getClientId();

  // Walter 2026-06-13: snapshot the version that is CURRENT at the moment of
  // resolution before clearing the band. Previously "mark as resolved" /
  // keep-current preserved nothing — so if later edits overwrote this
  // just-resolved version, there was no recoverable point (the class of
  // silent loss Walter hit: resolve → keep editing → overwrite, with nothing
  // snapshotted). Now every resolution leaves a labelled recoverable
  // snapshot of what was kept.
  await runTransaction(db, async (t) => {
    const snap = await t.get(ref);
    const cur = snap.exists() ? snap.data() : {};
    t.set(newHistRef, {
      content: cur.content ?? '',
      title: cur.title ?? '',
      prevUpdatedAt: cur.updatedAt ?? null,
      // Walter 2026-06-15: stamp replacedAt too. fetchHistoryEntries
      // orders by replacedAt, and Firestore's orderBy EXCLUDES docs that
      // lack the field — so a resolution snapshot written with only
      // snapshotAt never showed up in the panel, defeating its purpose.
      replacedAt: serverTimestamp(),
      snapshotAt: serverTimestamp(),
      byUid: actorUid || null,
      byClientId: clientId,
      // "the version that was current when the user resolved the conflict"
      reason: 'resolution',
    });
    const patch = {
      conflictPendingReview: null,
      updatedAt: serverTimestamp(),
      historyCount: increment(1),
      lastWriteByClientId: clientId,
    };
    if (typeof cleanContent === 'string') patch.content = cleanContent;
    t.set(ref, patch, { merge: true });
  });
  pruneHistoryToCap({ uid, boardId, listId });
}

/**
 * fetchHistoryEntries — one-shot read of a list's history subcollection,
 * newest first. Used by HistoryPanel which only loads when opened.
 * Capped at MAX entries to keep payload bounded.
 */
export async function fetchHistoryEntries({ uid, boardId, listId, max = 50 }) {
  if (!uid || !boardId || !listId) return [];
  const histColRef = collection(
    db, 'users', uid, 'boards', boardId, 'lists', listId, 'history',
  );
  const q = query(histColRef, orderBy('replacedAt', 'desc'), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
